"use client";

import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { normalizeIngredientText } from "@/lib/categorization/ingredient-normalization";
import { parseBulkIngredients } from "@/lib/categorization/utils";
import { ingredientSchemas, validate } from "@/lib/validation";

interface IngredientInputProps {
  onAdd: (values: string[]) => number; // Returns count of items added (handles single or multiple)
  // Voice input state (passed from parent)
  isListening?: boolean;
  isProcessing?: boolean;
  voiceTranscript?: string; // Transcript from parent voice recognition
  // Imported ingredients from URL
  importedIngredients?: string[];
}

export function IngredientInput({
  onAdd,
  isListening = false,
  isProcessing = false,
  voiceTranscript,
  importedIngredients,
}: IngredientInputProps) {
  const t = useTranslations("ingredients");
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Handle voice transcript updates from parent
  useEffect(() => {
    if (voiceTranscript) {
      // Voice-specific enhancement: normalize immediately for instant visual feedback
      const normalized = normalizeIngredientText(voiceTranscript);

      console.log("🎤 Voice transcript normalized:", normalized);

      // Append normalized text to textarea (gives instant visual feedback)
      setValue((prev) => (prev ? `${prev}\n${normalized}` : normalized));

      // Scroll after state update completes
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
      });
    }
  }, [voiceTranscript]);

  // Handle imported ingredients from URL
  useEffect(() => {
    if (importedIngredients && importedIngredients.length > 0) {
      // Join imported ingredients with newlines
      const importedText = importedIngredients.join("\n");

      // Append to existing text or set as new
      setValue((prev) => (prev ? `${prev}\n${importedText}` : importedText));

      // Scroll to bottom after state update completes
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
      });
    }
  }, [importedIngredients]);

  const handleAdd = () => {
    // Parse the input for bulk ingredients
    // Note: normalization already happened for voice input (instant feedback)
    // For manual text input, normalization happens inside parseBulkIngredients
    const ingredients = parseBulkIngredients(value);

    if (ingredients.length === 0) return;

    // Validate each ingredient
    for (const ingredient of ingredients) {
      const validationResult = validate(ingredientSchemas.text, ingredient);
      if (!validationResult.success) {
        // Extract key from "validation.{key}" format
        const errorKey = validationResult.error.replace("validation.", "");
        toast.error(t(errorKey as any));
        return;
      }
    }

    // Use the unified onAdd function (handles both single and multiple ingredients)
    const count = onAdd(ingredients);
    if (count > 0) {
      setValue(""); // Clear input if at least one was added
    }
  };

  const handleKeyDown = (_e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter without Shift adds ingredients
    // if (e.key === "Enter" && !e.shiftKey) {
    //   e.preventDefault(); // Prevent newline
    //   handleAdd();
    // }
    // Shift+Enter allows multi-line input
  };

  return (
    <div className="flex gap-2 items-start">
      <div className="relative flex-1">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isListening ? t("listening") : t("addPlaceholder")}
          className="text-base pr-10"
          rows={1}
          disabled={isListening || isProcessing}
        />
        {value && !isListening && !isProcessing && (
          <button
            type="button"
            className="absolute end-3.5 top-3.5 flex size-5 cursor-pointer items-center justify-center rounded-full bg-muted-foreground/30 text-background hover:bg-muted-foreground/50"
            onClick={() => setValue("")}
            aria-label={t("clear")}
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
      <Button
        onClick={handleAdd}
        disabled={!value.trim()}
        className="gap-2 px-4 md:px-8 disabled:opacity-100 disabled:bg-[#F2C9C0] disabled:text-[#B85544] dark:disabled:opacity-50 dark:disabled:bg-primary dark:disabled:text-white"
      >
        <span>{t("add")}</span>
      </Button>
    </div>
  );
}
